import PropTypes from "prop-types"
import React, { useState } from "react"
import { useQueryClient } from "react-query"

import Badge from "src/components/Badge"
import Button from "src/components/Button"
import OverflowMenu from "src/components/OverflowMenu"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { mutateApi } from "src/utils/api/ApiMethods"

const PaymentMethodPanel = ({
  paymentMethods,
  paymentMethodLinkPath,
  paymentMethodsPath,
}) => {
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [pendingId, setPendingId] = useState(null)
  const showToast = useToast()
  const tracker = useTracker()
  const queryClient = useQueryClient()

  const hasMethods = paymentMethods.length > 0
  const isBusy = isRedirecting || pendingId !== null

  const handleUpdatePressed = async () => {
    tracker.trackEvent("manage_subscriptions:update_payment_method_pressed")
    setIsRedirecting(true)

    try {
      const { link } = await mutateApi({
        url: paymentMethodLinkPath,
        method: "POST",
      })
      window.location.href = link
    } catch (error) {
      setIsRedirecting(false)
      showToast(
        error.message ||
          "We couldn't open the payment method page. Please try again.",
        { type: "error" }
      )
    }
  }

  const handleMakeDefault = async (paymentMethod) => {
    tracker.trackEvent(
      "manage_subscriptions:set_default_payment_method_pressed"
    )
    setPendingId(paymentMethod.id)

    try {
      await mutateApi({
        url: `${paymentMethodsPath}/${paymentMethod.id}`,
        method: "PATCH",
      })
      await queryClient.invalidateQueries(["marinaSubscription"])
    } catch (error) {
      showToast(
        error.message ||
          "We couldn't update your default payment method. Please try again.",
        { type: "error" }
      )
    } finally {
      setPendingId(null)
    }
  }

  const handleRemove = async (paymentMethod) => {
    if (!window.confirm(`Remove ${paymentMethod.title}?`)) return

    tracker.trackEvent("manage_subscriptions:remove_payment_method_pressed")
    setPendingId(paymentMethod.id)

    try {
      await mutateApi({
        url: `${paymentMethodsPath}/${paymentMethod.id}`,
        method: "DELETE",
      })
      await queryClient.invalidateQueries(["marinaSubscription"])
    } catch (error) {
      showToast(
        error.message ||
          "We couldn't remove that payment method. Please try again.",
        { type: "error" }
      )
    } finally {
      setPendingId(null)
    }
  }

  return (
    <section className="rounded border border-gray-200 bg-white px-5 pb-5 pt-2">
      <h2 className="mb-3 text-lg font-semibold">
        {paymentMethods.length > 1 ? "Payment methods" : "Payment method"}
      </h2>

      {hasMethods ? (
        <>
          <ul className="m-0 list-none divide-y divide-gray-100 pl-0">
            {paymentMethods.map((paymentMethod) => (
              <li
                key={paymentMethod.id}
                className="flex flex-row items-center gap-3 py-3 first:pt-0"
              >
                <div className="flex items-center justify-center">
                  <img
                    src={paymentMethod.logo}
                    alt="payment method brand logo"
                    className="w-8"
                  />
                </div>
                <div className="flex flex-col">
                  <h5 className="m-0 text-sm font-semibold">
                    {paymentMethod.title}
                  </h5>
                  <span className="text-xs text-gray-600">
                    {paymentMethod.subtitle}
                  </span>
                </div>
                {paymentMethod.default && (
                  <Badge text="Default" color="neutral" variant="small" />
                )}
                {!paymentMethod.default && (
                  <div className="ml-auto">
                    <OverflowMenu variant="transparent">
                      <OverflowMenu.Item
                        label="Make default"
                        disabled={isBusy}
                        onClick={() => handleMakeDefault(paymentMethod)}
                      />
                      <OverflowMenu.Item
                        label="Remove"
                        disabled={isBusy}
                        onClick={() => handleRemove(paymentMethod)}
                      />
                    </OverflowMenu>
                  </div>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-3 border-t border-gray-100 pt-3">
            <Button
              variant="tertiary"
              small
              isLoading={isRedirecting}
              disabled={isBusy}
              onClick={handleUpdatePressed}
            >
              Add payment method
            </Button>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-start gap-3">
          <p className="m-0 text-gray-600">No payment method on file.</p>
          <Button
            variant="tertiary"
            small
            isLoading={isRedirecting}
            disabled={isBusy}
            onClick={handleUpdatePressed}
          >
            Add payment method
          </Button>
        </div>
      )}
    </section>
  )
}

PaymentMethodPanel.propTypes = {
  paymentMethods: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      default: PropTypes.bool,
      logo: PropTypes.string,
      title: PropTypes.string,
      subtitle: PropTypes.string,
    })
  ).isRequired,
  paymentMethodLinkPath: PropTypes.string.isRequired,
  paymentMethodsPath: PropTypes.string.isRequired,
}

export default PaymentMethodPanel
